import React, { useState, useEffect } from 'react';
import './Order.css';
import { useNavigate } from 'react-router-dom';
import {
  FaFileInvoice, FaSignature, FaThumbsUp, FaTruck,
  FaCalendarAlt, FaMapMarkerAlt, FaBoxOpen, FaChartBar, FaTools
} from 'react-icons/fa';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Navbar from './components/Navbar';

const TABS = [
  { key: 'estimation',      label: 'Estimation',      icon: <FaChartBar /> },
  { key: 'invoice',         label: 'Invoice',          icon: <FaFileInvoice /> },
  { key: 'contractSign',    label: 'Contract Sign',    icon: <FaSignature /> },
  { key: 'pendingApproval', label: 'Pending Approval', icon: <FaThumbsUp /> },
  { key: 'confirmedOrders', label: 'Confirmed Orders', icon: <FaTruck /> },
];

const Orders = () => {
  const navigate   = useNavigate();
  const [activeTab, setActiveTab] = useState('estimation');
  const [orders, setOrders]       = useState([]);
  const [isLoaded, setIsLoaded]   = useState(false);
  const db   = getFirestore();
  const auth = getAuth();

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'ClientContactInfo', user.uid));
        if (snap.exists()) { setOrders(snap.data().orders || []); setIsLoaded(true); }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [auth, db]);

  // ── Helpers ─────────────────────────────────────────────────────────────

  const calcDays = (o) => {
    if (o.dateType === 'range' && o.from && o.to)
      return Math.max(1, Math.round((new Date(o.to) - new Date(o.from)) / 86400000) + 1);
    return 1;
  };

  const formatDate = (o) => {
    if (o.dateType === 'range' && o.from && o.to) {
      const f = new Date(`${o.from}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      const t = new Date(`${o.to}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
      return { label: `${f} – ${t}`, isRange: true };
    }
    if (o.date) {
      return {
        label: new Date(`${o.date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }),
        isRange: false
      };
    }
    return { label: 'No date', isRange: false };
  };

  const derive = (o) => {
    const days         = calcDays(o);
    const dailyMeals   = Math.max(0, Number(o.totalMeals)  || 0);
    const dailySpec    = Math.max(0, Number(o.specialMeals) || 0);
    const totalMeals   = dailyMeals * days;
    const specialMeals = Math.min(dailySpec * days, totalMeals);
    const regularMeals = totalMeals - specialMeals;
    const cutlery      = o.cutlery          ? Math.max(0, Number(o.cutleryQuantity)          || 0) * days : 0;
    const bowls        = o.paperBowl        ? Math.max(0, Number(o.paperBowlQuantity)        || 0) * days : 0;
    const containers   = o.plasticContainer ? Math.max(0, Number(o.plasticContainerQuantity) || 0) * days : 0;
    return { days, dailyMeals, totalMeals, specialMeals, regularMeals, cutlery, bowls, containers };
  };

  const summary = orders.reduce((a, o) => {
    const d = derive(o);
    a.totalOrders++; a.totalDays += d.days;
    a.totalMeals += d.totalMeals; a.specialMeals += d.specialMeals; a.regularMeals += d.regularMeals;
    return a;
  }, { totalOrders: 0, totalDays: 0, totalMeals: 0, specialMeals: 0, regularMeals: 0 });

  // ── Render ───────────────────────────────────────────────────────────────

  const renderEstimation = () => (
    <>
      {!isLoaded && <p className="est-loading">Loading orders…</p>}

      {isLoaded && orders.length === 0 && (
        <div className="est-empty">
          <FaChartBar className="est-empty-icon" />
          <h3>No orders yet</h3>
          <p>Add orders to see your estimation here.</p>
        </div>
      )}

      {isLoaded && orders.length > 0 && (
        <div className="est-grid">
          {orders.map((order, index) => {
            const { days, dailyMeals, totalMeals, specialMeals, regularMeals, cutlery, bowls, containers } = derive(order);
            const { label, isRange } = formatDate(order);
            const isPickup  = order.address === 'Pick up' || order.postcode === 'Pick up';
            const hasExtras = cutlery > 0 || bowls > 0 || containers > 0;

            return (
              <div className="est-card" key={index}>

                {/* Header */}
                <div className="est-card-top">
                  <div className="est-date">
                    <FaCalendarAlt className="est-date-icon" />
                    <div className="est-date-info">
                      <span className="est-date-label">{label}</span>
                      {isRange && <span className="est-days-badge">{days} days</span>}
                    </div>
                  </div>
                  <span className={`est-badge${order.isApproved ? ' est-badge--green' : ' est-badge--amber'}`}>
                    {order.isApproved ? 'Approved' : 'Pending'}
                  </span>
                </div>

                {/* Venue */}
                <div className={`est-venue${isPickup ? ' est-venue--pickup' : ''}`}>
                  <FaMapMarkerAlt className="est-venue-icon" />
                  <span className="est-venue-addr">{isPickup ? 'Pick up' : order.address}</span>
                  {!isPickup && order.postcode && <span className="est-postcode">{order.postcode}</span>}
                </div>

                {/* Meal blocks */}
                <div className="est-meals">
                  <div className="est-meal est-meal--total">
                    <span className="est-meal-num">{totalMeals}</span>
                    <span className="est-meal-name">
                      Total Meals
                      {isRange && <em>{dailyMeals}/day</em>}
                    </span>
                  </div>
                  <div className="est-meal est-meal--regular">
                    <span className="est-meal-num">{regularMeals}</span>
                    <span className="est-meal-name">Regular</span>
                  </div>
                  <div className="est-meal est-meal--special">
                    <span className="est-meal-num">{specialMeals}</span>
                    <span className="est-meal-name">Special</span>
                  </div>
                </div>

                {/* Extras */}
                {hasExtras && (
                  <div className="est-extras">
                    <FaBoxOpen className="est-extras-icon" />
                    {cutlery    > 0 && <span className="est-extra">Cutlery ×{cutlery}{isRange && <em> ({order.cutleryQuantity}/day)</em>}</span>}
                    {bowls      > 0 && <span className="est-extra">Bowls ×{bowls}{isRange && <em> ({order.paperBowlQuantity}/day)</em>}</span>}
                    {containers > 0 && <span className="est-extra">Containers ×{containers}{isRange && <em> ({order.plasticContainerQuantity}/day)</em>}</span>}
                  </div>
                )}

                {order.packingInstructions && (
                  <div className="est-note"><strong>Note:</strong> {order.packingInstructions}</div>
                )}

              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const renderUnderConstruction = () => (
    <div className="est-construction">
      <FaTools className="est-construction-icon" />
      <h3>Coming Soon</h3>
      <p>This section is under development.</p>
    </div>
  );

  const renderContent = () => {
    if (activeTab === 'estimation') return renderEstimation();
    if (activeTab === 'invoice')    { navigate('/Invoice'); return null; }
    return renderUnderConstruction();
  };

  return (
    <div className="orders-page">
      <Navbar title="Orders & Estimation" />

      {/* ── Hero: stats + tabs ──────────────────────────────────────────── */}
      <div className="orders-hero">
        <div className="orders-hero-inner">

          {/* Title row */}
          <div className="orders-hero-title">
            <h1>Orders &amp; Estimation</h1>
            <p>Overview of your meal orders and quantities</p>
          </div>

          {/* Stats — only on estimation tab with data */}
          {activeTab === 'estimation' && isLoaded && orders.length > 0 && (
            <div className="orders-stats">
              {[
                { val: summary.totalOrders,                  lbl: 'Orders' },
                { val: summary.totalDays,                    lbl: 'Total Days' },
                { val: summary.totalMeals.toLocaleString(),  lbl: 'Total Meals',   accent: true },
                { val: summary.regularMeals.toLocaleString(),lbl: 'Regular' },
                { val: summary.specialMeals.toLocaleString(),lbl: 'Special' },
              ].map(({ val, lbl, accent }) => (
                <div className="orders-stat" key={lbl}>
                  <span className={`orders-stat-val${accent ? ' orders-stat-val--green' : ''}`}>{val}</span>
                  <span className="orders-stat-lbl">{lbl}</span>
                </div>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="orders-tabs">
            {TABS.map(({ key, label, icon }) => (
              <button
                key={key}
                className={`orders-tab${activeTab === key ? ' orders-tab--active' : ''}`}
                onClick={() => key === 'invoice' ? navigate('/Invoice') : setActiveTab(key)}
              >
                {icon} {label}
              </button>
            ))}
          </div>

        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <div className="orders-body">
        {renderContent()}
      </div>
    </div>
  );
};

export default Orders;
